'use client';

import { c as _c } from "react/compiler-runtime";
import { Header as Shell } from '@sprd/spreadshirt-shell';
import { ReactNode, useMemo } from 'react';
import { useAtom } from 'jotai';
import { AmplienceImage, CustomerAttentionToolSlot } from '@/types/amplience';
import { HeaderContent } from '@/types/headerHierarchy';
import { HeaderContentPro } from '@/types/spreadshirtProHeader';
import { useNavActiveState } from '@/hooks/useNavActiveState';
import { useVisualViewportHeight } from '@/hooks/useVisualViewportHeight';
import { PageOverlay } from '@/components/Common/PageOverlay';
import { FetchUser } from '@/components/Header/FetchUser';
import { LazyAmplienceResponsiveImage } from '@/components/ResponsiveImage/LazyAmplienceResponsiveImage';
import { usePathname } from '@/i18n/navigation';
import { headerPageOverlayAtom } from '@/store/atoms';
import { hasEnoughTrustpilotStars } from '@/utility/hasEnoughTrustpilotStars';
import { MenuSlider } from '../MenuSlider/MenuSlider';
import { MiniBasket } from '../MiniBasket/MiniBasket';
import { DesktopHeader } from './DesktopHeader/DesktopHeader';
import { MobileHeader } from './MobileHeader/MobileHeader';
import { NavRenderers, toNavItems, toNavTeaser } from './headerContent';
const renderImage = (image: AmplienceImage) => <LazyAmplienceResponsiveImage source={{
  src: image.image,
  sizes: '64px'
}} alt={image.altText ?? ''} />;

// The same tree is rendered by both navs, but the teaser slider differs per viewport.
const desktopRenderers: NavRenderers = {
  image: renderImage,
  teaser: teaser => <MenuSlider tiles={teaser.tiles} className="[--slides-to-show:4.15]" showNavigation noPadding />
};
const mobileRenderers: NavRenderers = {
  image: renderImage,
  teaser: teaser => <MenuSlider tiles={teaser.tiles} className="[--slides-to-show:2.5]" />
};
export default function Header(t0) {
  const $ = _c(40);
  const {
    content,
    amplienceMediaBaseUrl,
    customerAttentionToolSlot,
    headerBanner,
    subDomain,
    sprdAccountsContext,
    trustpilotBanner,
    trustpilotStars
  } = t0;
  useVisualViewportHeight();
  const pathname = usePathname();
  const [showOverlay] = useAtom(headerPageOverlayAtom);
  const isDesignerPage = pathname === "/cyo/designer";
  const active = useNavActiveState(content);
  let t1;
  if ($[0] !== content) {
    t1 = toNavItems(content, desktopRenderers);
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const desktopItems = t1;
  let t2;
  if ($[2] !== content) {
    t2 = toNavItems(content, mobileRenderers);
    $[2] = content;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const mobileItems = t2;
  let t3;
  if ($[4] !== content.content.headerTeaserSlot) {
    t3 = toNavTeaser(content.content.headerTeaserSlot, mobileRenderers.teaser);
    $[4] = content.content.headerTeaserSlot;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const mobileRootTeaser = t3;
  const t4 = hasEnoughTrustpilotStars(trustpilotStars) ? trustpilotBanner : null;
  const t5 = !isDesignerPage;
  let t6;
  if ($[6] !== active || $[7] !== content || $[8] !== desktopItems || $[9] !== isDesignerPage || $[10] !== pathname || $[11] !== sprdAccountsContext || $[12] !== subDomain || $[13] !== t5) {
    t6 = <DesktopHeader content={content} items={desktopItems} active={active} resetKey={pathname} openOnHover={t5} isDesignerPage={isDesignerPage} subDomain={subDomain} sprdAccountsContext={sprdAccountsContext} />;
    $[6] = active;
    $[7] = content;
    $[8] = desktopItems;
    $[9] = isDesignerPage;
    $[10] = pathname;
    $[11] = sprdAccountsContext;
    $[12] = subDomain;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== active || $[16] !== content || $[17] !== customerAttentionToolSlot || $[18] !== isDesignerPage || $[19] !== mobileItems || $[20] !== mobileRootTeaser || $[21] !== pathname || $[22] !== sprdAccountsContext || $[23] !== subDomain) {
    t7 = <MobileHeader content={content} items={mobileItems} active={active} resetKey={pathname} rootTeaser={mobileRootTeaser} isDesignerPage={isDesignerPage} subDomain={subDomain} sprdAccountsContext={sprdAccountsContext} customerAttentionToolSlot={customerAttentionToolSlot} />;
    $[15] = active;
    $[16] = content;
    $[17] = customerAttentionToolSlot;
    $[18] = isDesignerPage;
    $[19] = mobileItems;
    $[20] = mobileRootTeaser;
    $[21] = pathname;
    $[22] = sprdAccountsContext;
    $[23] = subDomain;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== showOverlay) {
    t8 = <PageOverlay isShown={showOverlay} />;
    $[25] = showOverlay;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FetchUser />;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8) {
    t10 = <>{t8}{t9}</>;
    $[28] = t8;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== amplienceMediaBaseUrl) {
    t11 = <MiniBasket amplienceMediaBaseUrl={amplienceMediaBaseUrl} />;
    $[30] = amplienceMediaBaseUrl;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== headerBanner || $[33] !== isDesignerPage || $[34] !== t10 || $[35] !== t11 || $[36] !== t4 || $[37] !== t6 || $[38] !== t7) {
    t12 = <Shell.Root pinned={isDesignerPage} banner={headerBanner} mobileBanner={t4} desktop={t6} mobile={t7} after={t10}>{t11}</Shell.Root>;
    $[32] = headerBanner;
    $[33] = isDesignerPage;
    $[34] = t10;
    $[35] = t11;
    $[36] = t4;
    $[37] = t6;
    $[38] = t7;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}